import React, { useState } from 'react';
import { 
  Box,
  Button, 
  FormControl, 
  FormLabel, 
  Input, 
  Textarea,
  Heading, 
  Text,
  useToast,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  Radio,
  RadioGroup,
  Stack,
  Flex,
  Alert,
  AlertIcon,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td
} from '@chakra-ui/react';
import { useWeb3 } from '../lib/Web3Provider';
import { extractErrorMessage, parseAddressList, isValidAddress } from '../lib/utils';

const MintingForm: React.FC = () => {
  const { writeContract } = useWeb3();
  const [tokenId, setTokenId] = useState('');
  const [mintType, setMintType] = useState('single');
  const [recipient, setRecipient] = useState('');
  const [recipients, setRecipients] = useState('');
  const [amount, setAmount] = useState(1);
  const [isMinting, setIsMinting] = useState(false);
  const [mintSuccess, setMintSuccess] = useState(false);
  const [parsedAddresses, setParsedAddresses] = useState<string[]>([]);
  const [validationError, setValidationError] = useState('');
  const toast = useToast();

  const handleRecipientListChange = (value: string) => {
    setRecipients(value);
    
    // Parse and validate addresses
    const addrs = parseAddressList(value);
    setParsedAddresses(addrs);
    
    // Check for invalid addresses
    const invalidAddrs = addrs.filter(addr => !isValidAddress(addr));
    if (invalidAddrs.length > 0) {
      setValidationError(`Invalid address format: ${invalidAddrs[0]}${invalidAddrs.length > 1 ? ` and ${invalidAddrs.length - 1} more` : ''}`);
    } else {
      setValidationError('');
    }
  };

  const handleMint = async () => {
    if (!writeContract) {
      toast({
        title: 'Error',
        description: 'Contract not loaded, please connect your wallet',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    if (!tokenId) {
      toast({
        title: 'Error',
        description: 'Please enter a token ID',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    setIsMinting(true);
    try {
      let tx;

      if (mintType === 'single') {
        // Single recipient mint
        if (!recipient || !isValidAddress(recipient)) {
          throw new Error('Please enter a valid recipient address');
        }

        tx = await writeContract['mint(address,uint256,uint256,bytes)'](
          recipient,
          tokenId,
          amount,
          []
        );
      } else {
        // Multiple recipients mint
        const addresses = parseAddressList(recipients);
        
        if (addresses.length === 0) {
          throw new Error('Please enter at least one recipient address');
        }
        
        // Validate all addresses
        const invalidAddr = addresses.find(addr => !isValidAddress(addr));
        if (invalidAddr) {
          throw new Error(`Invalid address format: ${invalidAddr}`);
        }

        tx = await writeContract['mint(address[],uint256,bytes)'](
          addresses,
          tokenId,
          []
        );
      }

      // Wait for transaction confirmation
      await tx.wait();
      
      setMintSuccess(true);
      toast({
        title: 'Success',
        description: `Successfully minted token${mintType === 'multiple' ? 's' : ''}`,
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
    } catch (error) {
      console.error('Error minting tokens:', error);
      toast({
        title: 'Minting Failed',
        description: extractErrorMessage(error),
        status: 'error',
        duration: 7000,
        isClosable: true,
      });
    } finally {
      setIsMinting(false);
    }
  };

  const resetForm = () => {
    setMintSuccess(false);
    setTokenId('');
    setRecipient('');
    setRecipients('');
    setAmount(1);
  };

  return (
    <Box>
      <Heading as="h2" size="lg" mb={5}>
        Mint and Distribute Tokens
      </Heading>
      
      <Text mb={6}>
        Mint tokens to individual addresses or distribute them to multiple recipients at once.
      </Text>
      
      {mintSuccess ? (
        <Alert
          status="success"
          variant="subtle"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          textAlign="center"
          height="200px"
          mb={5}
        >
          <AlertIcon boxSize="40px" mr={0} />
          <Heading as="h3" size="md" mt={4} mb={2}>
            Tokens Minted Successfully!
          </Heading>
          <Text mb={4}>
            The tokens have been minted and distributed to the recipient{mintType === 'multiple' ? 's' : ''}.
          </Text>
          <Button 
            colorScheme="blue" 
            onClick={resetForm}
          >
            Mint More Tokens
          </Button>
        </Alert>
      ) : (
        <>
          <FormControl mb={4}>
            <FormLabel>Token ID</FormLabel>
            <Input 
              placeholder="Enter the token ID to mint"
              value={tokenId}
              onChange={(e) => setTokenId(e.target.value)}
            />
          </FormControl>
          
          <FormControl as="fieldset" mb={6}>
            <FormLabel as="legend">Minting Type</FormLabel>
            <RadioGroup value={mintType} onChange={setMintType}>
              <Stack direction="row">
                <Radio value="single">Single Recipient</Radio>
                <Radio value="multiple">Multiple Recipients</Radio>
              </Stack>
            </RadioGroup>
          </FormControl>
          
          {mintType === 'single' ? (
            <>
              <FormControl mb={4}>
                <FormLabel>Recipient Address</FormLabel>
                <Input 
                  placeholder="0x..."
                  value={recipient}
                  onChange={(e) => setRecipient(e.target.value)}
                />
              </FormControl>
              
              <FormControl mb={6}>
                <FormLabel>Amount</FormLabel>
                <NumberInput 
                  min={1} 
                  value={amount} 
                  onChange={(_, valueAsNumber) => setAmount(valueAsNumber)}
                >
                  <NumberInputField />
                  <NumberInputStepper>
                    <NumberIncrementStepper />
                    <NumberDecrementStepper />
                  </NumberInputStepper>
                </NumberInput>
              </FormControl>
            </>
          ) : (
            <FormControl mb={4}>
              <FormLabel>Recipient Addresses</FormLabel>
              <Textarea
                placeholder="Enter addresses separated by commas, spaces, or new lines"
                value={recipients}
                onChange={(e) => handleRecipientListChange(e.target.value)}
                rows={6}
              />
              {validationError && (
                <Text color="red.500" mt={2} fontSize="sm">
                  {validationError}
                </Text>
              )}
              
              {parsedAddresses.length > 0 && !validationError && (
                <Box mt={4}>
                  <Text mb={2} fontWeight="medium">
                    Recipients ({parsedAddresses.length}):
                  </Text>
                  <Box maxH="200px" overflowY="auto" borderWidth="1px" borderRadius="md" p={2}>
                    <Table size="sm" variant="simple">
                      <Thead>
                        <Tr>
                          <Th>#</Th>
                          <Th>Address</Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {parsedAddresses.map((addr, idx) => (
                          <Tr key={idx}>
                            <Td>{idx + 1}</Td>
                            <Td fontFamily="monospace">{addr}</Td>
                          </Tr>
                        ))}
                      </Tbody>
                    </Table>
                  </Box>
                </Box>
              )}
            </FormControl>
          )}
          
          <Flex justifyContent="center" mt={8}>
            <Button 
              colorScheme="blue" 
              size="lg"
              isLoading={isMinting}
              loadingText="Minting..."
              onClick={handleMint}
              isDisabled={!tokenId || (mintType === 'single' ? !recipient : parsedAddresses.length === 0 || !!validationError)}
            >
              Mint Tokens
            </Button>
          </Flex>
        </>
      )}
    </Box>
  );
};

export default MintingForm;